/**
 * consequence — next-step clarity copy for every proposal card (goal-loop
 * amendment 7, 2026-07-05).
 *
 * Two rules, one place:
 *   1. CONSEQUENCE LINE — every proposal (work item, Play, config) states on
 *      the card what approving does, when it will act, and what you'll see
 *      next. These helpers hold the shared wordings so the same beat reads
 *      identically in the case-file gate, the Feed cards, and (task 4) the
 *      config-proposal cards in DecisionsQueue.
 *   2. HANDED-OFF FLIP — on approval the card flips in place to its
 *      handed-off state ("Executing…", "Scheduled — first run Mon 9:00am")
 *      for {@link HANDOFF_FLIP_MS} before leaving the lane, so the baton pass
 *      is always visible.
 */

import {
  COMMISSION_PROJECT_CONSEQUENCE,
  COMMISSION_PROJECT_RECEIPT,
  COMMISSION_PROJECT_VERB,
  MILESTONE_DECOMPOSITION_GATE_KIND,
  MILESTONE_PUSH_CONSEQUENCE,
  MILESTONE_PUSH_RECEIPT,
  MILESTONE_PUSH_VERB,
} from "@yagni/shared";

/** How long an approved card holds its handed-off state before advancing. */
export const HANDOFF_FLIP_MS = 1600;

/** The work-item execution consequence (approve-the-plan gates). */
export const EXECUTION_CONSEQUENCE =
  "Approving starts execution now — you'll get a receipt when it verifies.";

/** The handed-off state an approved execution flips to. */
export const EXECUTING_RECEIPT =
  "Executing now — a receipt lands here when the work verifies.";

// ── The Project commissioning gate (ADR-0024 step 4, YAG-338 wave 3) ────────
//
// Re-exported from @yagni/shared rather than written here, because this one
// Decision renders from two copy sources: the Feed's effect line is computed
// server-side in feed/aggregate.ts, and the case file reads this module. Every
// gate kind that predates it has two slightly different sentences for the same
// act as a result. Commissioning does not, and the only way to keep that true
// is for both sides to import the same string.
//
// What the line does NOT say is as deliberate as what it does. It grants
// standing permission to argue work toward the Project and stops there: nothing
// reaches a customer's tracker, and the separate push confirm ADR-0024 step 5
// describes is not built yet, so the line must not point at a button that is not
// there. And no cost, capacity or credit appears anywhere near it (ADR-0027
// removed the draw from every gate on every surface).

export {
  COMMISSION_PROJECT_CONSEQUENCE,
  COMMISSION_PROJECT_RECEIPT,
  COMMISSION_PROJECT_VERB,
  // The SECOND confirm (ADR-0024 step 5, route built in wave 4). The only
  // moment in the Project flow when anything leaves YAGNI, and therefore the
  // only line permitted to say a tracker is written.
  MILESTONE_DECOMPOSITION_GATE_KIND,
  MILESTONE_PUSH_CONSEQUENCE,
  MILESTONE_PUSH_RECEIPT,
  MILESTONE_PUSH_VERB,
};

/**
 * "Jun 20" from a Play grant's deadline ISO, or null when there is none.
 * Deadlines are stored as midnight UTC, so they must render in UTC too —
 * local-zone formatting shifted non-UTC operators' deadlines a day (YAG-152).
 */
export function formatPlayDeadline(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/** The Play consequence: what the one approval grants, and until when. */
export function playGrantConsequence(deadlineIso: string | null): string {
  const deadline = formatPlayDeadline(deadlineIso);
  return deadline
    ? `Approving grants this Play until ${deadline} — the team runs the whole plan and reports back when it has verified the result.`
    : "Approving grants this Play with no deadline — the team runs the whole plan and reports back when it has verified the result.";
}

/**
 * The handed-off state a scheduled approval flips to ("Scheduled — first run
 * Mon 9:00am"). `firstRun` is the next occurrence in plain words.
 */
export function scheduledReceipt(firstRun: string): string {
  return `Scheduled — first run ${firstRun}.`;
}

// ── Config proposals (goal-loop task 4 — a Team changing its own rhythms/
//    sources through the same gate as work; always staged, never auto). ──────

/** Rhythm create/update consequence: what approving does + when it first acts. */
export function rhythmScheduleConsequence(firstRun: string | null): string {
  return firstRun
    ? `Approving schedules this — first run ${firstRun}.`
    : "Approving schedules this — it runs on the proposed cadence.";
}

/** Pausing a rhythm is immediate — no first-run to point at. */
export const RHYTHM_PAUSE_CONSEQUENCE =
  "Approving pauses this rhythm — it stops firing until someone resumes it.";

export const RHYTHM_RESUME_CONSEQUENCE =
  "Approving resumes this rhythm — it fires again on its schedule.";

/** Source attach/detach consequence. */
export function sourceAttachConsequence(sourceLabel: string): string {
  return `Approving attaches ${sourceLabel} — the team can use it from its next turn.`;
}

export function sourceDetachConsequence(sourceLabel: string): string {
  return `Approving detaches ${sourceLabel} — the team stops reading it from its next turn.`;
}

/** The handed-off state an approved source attach flips to. */
export function sourceAttachedReceipt(sourceLabel: string): string {
  return `Attached — the team can use ${sourceLabel} from its next turn.`;
}

export function sourceDetachedReceipt(sourceLabel: string): string {
  return `Detached — the team stops reading ${sourceLabel} from its next turn.`;
}
